import { Box, Container, Divider, Link, Typography, useTheme, Alert } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMatch, useSearchParams } from 'react-router-dom';
import { SendCodeRequest } from 'apis/auth/types';
import { useNetsapiensLogin, useSendEmailForCode } from 'apis/auth/hook';
import Input from 'components/Common/Input';
import Button from 'components/Common/Button';
import { loginSchema } from './ValidationSchema';
import { getCookie, setCookie } from 'utils/helperFunctions';
import { CacheKeys } from 'utils/cacheKeys';
import { Navigate } from 'react-router-dom';
import { useGetOrganizations } from 'apis/agency/organizations/hook';
import Checkbox from 'components/Common/Checkbox';

export default function Login() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const isUnauthorized = searchParams.get('error') === 'unauthorized';
  const netsapiensOnlyMatch = useMatch('/:cspSlug/auth/login');
  const isNetsapiensOnly = Boolean(netsapiensOnlyMatch);
  const cspNameFromUrl = netsapiensOnlyMatch?.params.cspSlug;

  const isAuthenticated = getCookie(CacheKeys.isAuthenticated) === 'true';

  const { mutate: sendVerificationCode, isPending } = useSendEmailForCode();
  const { mutate: loginWithNetsapiens, isPending: isNetsapiensLoginPending } = useNetsapiensLogin();
  const { data: organizations } = useGetOrganizations();

  const cspName = cspNameFromUrl || (organizations?.configuration?.csp_name as string | undefined);

  useEffect(() => {
    if (organizations?.uid) {
      const isKickcallAgency = organizations.uid === 'AG316997';
      setCookie(CacheKeys.kickcallAgency, isKickcallAgency.toString());
    }
  }, [organizations?.uid]);

  const requireTerms =
    Boolean(organizations?.configuration?.terms_and_conditions_url) ||
    Boolean(organizations?.configuration?.privacy_policy_url);
  const { control, handleSubmit } = useForm<SendCodeRequest>({
    resolver: zodResolver(loginSchema(requireTerms)),
    mode: 'onBlur'
  });

  const onSubmit = (data: SendCodeRequest) => {
    //as per BE requirement
    data = { ...data, accepted_privacy: data.accepted_terms };
    sendVerificationCode(data);
  };

  if (isAuthenticated) {
    return <Navigate to='/' state={window.location.href} />;
  }

  return (
    <Container
      maxWidth='xs'
      style={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          textAlign: 'center'
        }}
      >
        {organizations?.logo_url && <img src={organizations?.logo_url} alt='app-logo' height={72} />}
        <Typography variant='h1' fontWeight={500} mt={3}>
          {isNetsapiensOnly ? 'Log In with Netsapiens' : 'Sign Up / Log In to Your Account'}
        </Typography>
        {isUnauthorized && (
          <Alert severity='error' sx={{ mt: 2, textAlign: 'left' }}>
            You are not authorized to access this application. Please contact your administrator.
          </Alert>
        )}
      </Box>
      <Box
        component={isNetsapiensOnly ? 'div' : 'form'}
        sx={{ width: '100%', maxWidth: '100%' }}
        noValidate
        autoComplete='off'
        onSubmit={isNetsapiensOnly ? undefined : handleSubmit(onSubmit)}
      >
        {!isNetsapiensOnly && (
          <>
            <Input
              name='email'
              label='Email address'
              type='email'
              margin='normal'
              control={control}
              fullWidth
              autoComplete='email'
            />
            {(organizations?.configuration?.terms_and_conditions_url ||
              organizations?.configuration?.privacy_policy_url) && (
              <Checkbox
                name='accepted_terms'
                control={control}
                label={
                  <Typography variant='body2'>
                    I agree to{' '}
                    {organizations?.configuration?.terms_and_conditions_url && (
                      <Link
                        href={organizations.configuration?.terms_and_conditions_url}
                        target='_blank'
                        underline='hover'
                        sx={{ color: theme.palette.primary.main }}
                      >
                        Terms and Conditions
                      </Link>
                    )}
                    {organizations?.configuration?.terms_and_conditions_url &&
                      organizations?.configuration?.privacy_policy_url &&
                      ' and '}
                    {organizations?.configuration?.privacy_policy_url && (
                      <Link
                        href={organizations.configuration?.privacy_policy_url}
                        target='_blank'
                        underline='hover'
                        sx={{ color: theme.palette.primary.main }}
                      >
                        Privacy Policy
                      </Link>
                    )}
                  </Typography>
                }
              />
            )}

            <Button type='submit' variant='contained' color='primary' fullWidth sx={{ mt: 3 }} isPending={isPending}>
              Next
            </Button>
          </>
        )}

        {cspName && (
          <>
            {!isNetsapiensOnly && <Divider sx={{ my: 2 }}>or</Divider>}
            <Button
              type='button'
              variant={isNetsapiensOnly ? 'contained' : 'outlined'}
              color='primary'
              fullWidth
              sx={{ mt: isNetsapiensOnly ? 3 : 0 }}
              isPending={isNetsapiensLoginPending}
              disabled={!cspName}
              onClick={() => loginWithNetsapiens({ csp_name: cspName })}
            >
              Login with Netsapiens
            </Button>
          </>
        )}
      </Box>
    </Container>
  );
}
